import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import { Play, X, ImageIcon, Film, MessageCircle, Send, Trash2, Loader2, ThumbsUp } from "lucide-react";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { PageHero } from "@/components/PageHero";
import { useAuth } from "@/context/AuthContext";
import { api, API } from "@/lib/api";
import { ShareOnX } from "@/components/ShareOnX";
import { XTimeline } from "@/components/XTimeline";

const HERO = "https://images.unsplash.com/photo-1523995462485-3d171b5c8fa9?auto=format&fit=crop&w=1600&q=80";

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.55, delay: i * 0.07 } }),
};

function ytId(url = "") {
  const m = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)([\w-]{11})/);
  return m ? m[1] : null;
}
function vimeoId(url = "") {
  const m = url.match(/vimeo\.com\/(?:video\/)?(\d+)/);
  return m ? m[1] : null;
}
function embedUrl(url = "") {
  const yt = ytId(url);
  if (yt) return `https://www.youtube.com/embed/${yt}?autoplay=1&rel=0`;
  const vm = vimeoId(url);
  if (vm) return `https://player.vimeo.com/video/${vm}?autoplay=1`;
  return url;
}
function thumbFor(m) {
  if (m.thumbnail_file_id) return `${API}/public/files/${m.thumbnail_file_id}`;
  if (m.media_type === "image" && m.file_id) return `${API}/public/files/${m.file_id}`;
  if (m.media_type === "video_link" && m.video_url) {
    const id = ytId(m.video_url);
    if (id) return `https://img.youtube.com/vi/${id}/hqdefault.jpg`;
  }
  return null;
}

export default function Media() {
  const [items, setItems] = useState([]);
  const [active, setActive] = useState(null);
  const [filter, setFilter] = useState("All");
  const [comments, setComments] = useState([]);
  const [text, setText] = useState("");
  const [posting, setPosting] = useState(false);
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/media").then(({ data }) => setItems(data)).catch(() => {});
  }, []);

  const loadComments = (id) => api.get(`/media/${id}/comments`).then(({ data }) => setComments(data)).catch(() => setComments([]));

  const openPost = (m) => { setActive(m); setComments([]); setText(""); loadComments(m.id); };
  const closeLightbox = () => { setActive(null); setComments([]); setText(""); };

  const postComment = async () => {
    if (!user) { toast.info("Please log in to comment."); navigate("/login"); return; }
    if (!text.trim()) return;
    setPosting(true);
    try {
      await api.post(`/media/${active.id}/comments`, { text: text.trim() });
      setText("");
      loadComments(active.id);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not post comment");
    } finally { setPosting(false); }
  };

  const deleteComment = async (id) => {
    try {
      await api.delete(`/media-comments/${id}`);
      loadComments(active.id);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not delete comment");
    }
  };

  const likeComment = async (id) => {
    if (!user) { toast.info("Please log in to like comments."); navigate("/login"); return; }
    try {
      const { data } = await api.post(`/media-comments/${id}/like`);
      setComments((cs) => {
        const updated = cs.map((c) => (c.id === id ? { ...c, like_count: data.like_count, liked_by_me: data.liked } : c));
        return [...updated].sort((a, b) => (b.like_count - a.like_count) || (new Date(a.created_at) - new Date(b.created_at)));
      });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not like comment");
    }
  };

  const tags = useMemo(() => {
    const set = new Set(items.map((m) => (m.tag || "").trim()).filter(Boolean));
    return ["All", ...Array.from(set)];
  }, [items]);

  const visible = filter === "All" ? items : items.filter((m) => (m.tag || "").trim() === filter);

  return (
    <div>
      <Navbar />

      {/* HERO */}
      <PageHero
        image={HERO}
        imageAlt="American flag"
        kicker="Watch & Follow"
        title={<>PPUSA <span className="text-[#D90429]">Media</span></>}
        subtitle="Videos, photos, and highlights from rallies, trainings, and the movement on the ground."
      />

      {/* GRID */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          {tags.length > 1 && (
            <div className="flex flex-wrap gap-2 mb-10" data-testid="media-filters">
              {tags.map((t) => (
                <button
                  key={t}
                  data-testid={`media-filter-${t.toLowerCase()}`}
                  onClick={() => setFilter(t)}
                  className={`px-5 py-2 text-xs uppercase tracking-[0.15em] font-bold transition-colors duration-200 ${
                    filter === t ? "bg-[#D90429] text-white" : "bg-slate-100 text-[#0A1128] hover:bg-slate-200"
                  }`}
                >
                  {t}
                </button>
              ))}
            </div>
          )}

          {visible.length === 0 ? (
            <p className="text-slate-400 text-center py-20">No media posted yet — check back soon.</p>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {visible.map((m, i) => {
                const thumb = thumbFor(m);
                const isImage = m.media_type === "image";
                return (
                  <motion.button
                    key={m.id}
                    initial="hidden" whileInView="show" viewport={{ once: true }} variants={fadeUp} custom={i}
                    data-testid="media-card"
                    onClick={() => openPost(m)}
                    className="group text-left bg-white border border-slate-200 hover:border-[#D90429] transition-colors duration-200 overflow-hidden flex flex-col"
                  >
                    <div className="relative h-52 bg-[#0A1128] overflow-hidden">
                      {thumb ? (
                        <img src={thumb} alt={m.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          {isImage ? <ImageIcon className="w-10 h-10 text-white/30" /> : <Film className="w-10 h-10 text-white/30" />}
                        </div>
                      )}
                      {!isImage && (
                        <div className="absolute inset-0 flex items-center justify-center">
                          <div className="w-16 h-16 rounded-full bg-[#D90429]/90 flex items-center justify-center group-hover:scale-110 transition-transform duration-200">
                            <Play className="w-7 h-7 text-white fill-white ml-1" />
                          </div>
                        </div>
                      )}
                    </div>
                    <div className="p-5 flex-1 flex flex-col">
                      {m.tag && <span className="text-[10px] uppercase tracking-[0.2em] text-[#D90429] font-bold mb-1.5">{m.tag}</span>}
                      <h3 className="font-display text-xl uppercase leading-tight text-[#0A1128]">{m.title}</h3>
                      {m.description && <p className="text-sm text-slate-500 mt-2 line-clamp-3">{m.description}</p>}
                    </div>
                  </motion.button>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* LIVE X FEED */}
      <section className="bg-[#0A1128] text-white py-20 grain stars relative">
        <div className="max-w-2xl mx-auto px-5 sm:px-8 relative z-10 text-center">
          <span className="text-sm uppercase tracking-[0.3em] font-semibold text-[#F4A261]">Live Feed</span>
          <h2 className="font-display text-4xl sm:text-5xl uppercase mt-3 mb-8">Latest From <span className="text-[#D90429]">X</span></h2>
          <div className="text-left">
            <XTimeline />
          </div>
        </div>
      </section>

      {/* LIGHTBOX */}
      <AnimatePresence>
        {active && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            data-testid="media-lightbox"
            className="fixed inset-0 z-[100] bg-black/90 flex items-start sm:items-center justify-center p-4 overflow-y-auto"
            onClick={closeLightbox}
          >
            <button data-testid="media-lightbox-close" onClick={closeLightbox} className="fixed top-5 right-5 z-10 text-white/70 hover:text-white"><X className="w-8 h-8" /></button>
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.95, opacity: 0 }}
              className="w-full max-w-4xl my-4" onClick={(e) => e.stopPropagation()}
            >
              <div className="bg-black">
                {active.media_type === "image" ? (
                  <img src={`${API}/public/files/${active.file_id}`} alt={active.title} className="w-full max-h-[70vh] object-contain" />
                ) : active.media_type === "video_upload" ? (
                  <video src={`${API}/public/media-video/${active.file_id}`} controls autoPlay className="w-full max-h-[70vh]" />
                ) : (
                  <div className="relative w-full" style={{ paddingBottom: "56.25%" }}>
                    <iframe
                      src={embedUrl(active.video_url)}
                      title={active.title}
                      className="absolute inset-0 w-full h-full"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                    />
                  </div>
                )}
              </div>
              <div className="bg-[#0A1128] text-white p-6">
                {active.tag && <span className="text-[10px] uppercase tracking-[0.2em] text-[#F4A261] font-bold">{active.tag}</span>}
                <h3 className="font-display text-2xl uppercase mt-1">{active.title}</h3>
                {active.description && <p className="text-white/70 text-sm mt-2 leading-relaxed">{active.description}</p>}
                <div className="mt-4">
                  <ShareOnX
                    testid={`share-on-x-${active.id}`}
                    text={`${active.title} — Patriot Power USA`}
                    url={`${window.location.origin}/media`}
                    className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.12em] font-bold text-white/70 hover:text-white transition-colors duration-200"
                  />
                </div>
              </div>

              {/* COMMENTS */}
              <div className="bg-white p-6" data-testid="media-comments">
                <div className="flex items-center gap-2 mb-5">
                  <MessageCircle className="w-5 h-5 text-[#D90429]" />
                  <h4 className="font-display text-lg uppercase text-[#0A1128]">
                    {active.comments_enabled === false ? "Comments Off" : `Comments (${comments.length})`}
                  </h4>
                </div>

                {active.comments_enabled === false ? (
                  <p className="text-slate-400 text-sm">Comments are turned off for this post.</p>
                ) : (
                  <>
                    {/* input */}
                    {user ? (
                      <div className="flex gap-3 mb-6" data-testid="comment-composer">
                        <div className="w-9 h-9 rounded-full bg-[#0A1128] text-white flex items-center justify-center font-display text-sm shrink-0 uppercase">
                          {(user.name || user.email || "?").charAt(0)}
                        </div>
                        <div className="flex-1">
                          <textarea
                            data-testid="comment-input"
                            value={text}
                            onChange={(e) => setText(e.target.value)}
                            placeholder="Add a comment…"
                            rows={2}
                            className="w-full border border-slate-300 px-3 py-2 text-sm rounded-none focus:outline-none focus:border-[#D90429] resize-none"
                          />
                          <button
                            data-testid="comment-submit"
                            onClick={postComment}
                            disabled={posting || !text.trim()}
                            className="mt-2 inline-flex items-center gap-2 bg-[#D90429] text-white px-5 py-2 uppercase tracking-[0.15em] text-xs font-bold hover:bg-[#b0031f] transition-colors duration-200 disabled:opacity-50"
                          >
                            {posting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Post
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="bg-slate-50 border border-slate-200 p-4 mb-6 text-sm text-slate-600" data-testid="comment-login-prompt">
                        <Link to="/login" className="text-[#D90429] font-semibold hover:underline">Log in</Link> as a member to join the conversation.
                      </div>
                    )}

                    {/* list */}
                    {comments.length === 0 ? (
                      <p className="text-slate-400 text-sm">No comments yet. Be the first to comment.</p>
                    ) : (
                      <div className="space-y-4">
                        {comments.map((c) => {
                          const canDelete = user && (user.role === "admin" || user.email === c.user_email);
                          return (
                            <div key={c.id} data-testid="comment-item" className="flex gap-3">
                              <div className={`w-9 h-9 rounded-full flex items-center justify-center font-display text-sm shrink-0 uppercase ${c.is_admin ? "bg-[#D90429] text-white" : "bg-slate-200 text-[#0A1128]"}`}>
                                {(c.user_name || "?").charAt(0)}
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 flex-wrap">
                                  <span className="font-semibold text-sm text-[#0A1128]">{c.user_name}</span>
                                  {c.is_admin && <span className="text-[9px] uppercase tracking-wider bg-[#0A1128] text-white px-1.5 py-0.5">Team</span>}
                                  <span className="text-xs text-slate-400">{new Date(c.created_at).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}</span>
                                  {canDelete && (
                                    <button data-testid={`comment-delete-${c.id}`} onClick={() => deleteComment(c.id)} className="text-slate-300 hover:text-[#D90429] ml-auto" title="Delete comment">
                                      <Trash2 className="w-3.5 h-3.5" />
                                    </button>
                                  )}
                                </div>
                                <p className="text-sm text-slate-700 mt-0.5 whitespace-pre-wrap break-words">{c.text}</p>
                                <button
                                  data-testid={`comment-like-${c.id}`}
                                  onClick={() => likeComment(c.id)}
                                  className={`inline-flex items-center gap-1.5 mt-1.5 text-xs font-semibold transition-colors duration-200 ${c.liked_by_me ? "text-[#D90429]" : "text-slate-400 hover:text-[#D90429]"}`}
                                >
                                  <ThumbsUp className={`w-3.5 h-3.5 ${c.liked_by_me ? "fill-[#D90429]" : ""}`} />
                                  {c.like_count > 0 ? c.like_count : ""} <span className="uppercase tracking-wide">{c.like_count === 1 ? "Like" : "Likes"}</span>
                                </button>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <Footer />
    </div>
  );
}
